import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Button from "src/base-components/Button";
import { useAppSelector } from "src/stores";
import { selectAuthUser } from "src/stores/auth";

function MillwoodsDocs() {
  const navigate = useNavigate();
  const authUser = useAppSelector(selectAuthUser);


 interface roleDataProps {

  user_guide_url?: string | null;
}
  const token = authUser?.token ?? null;
const userGuideUrl = (authUser?.role as roleDataProps & { user_guide_url?: string })?.user_guide_url ?? null;


  // 🟡 Still rehydrating from persist
  if (authUser === undefined) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <Spinner label="Checking session..." />
      </div>
    );
  }

  // 🔴 No token → show restricted UI inline (no navigate, no reload)
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 ">
        <Helmet>
          <meta charSet="utf-8" />
          <title>CRM User Guide | Millwoods Travels</title>
        </Helmet>

        <div className="bg-white border border-gray-200 rounded-xl p-10 max-w-sm w-full text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center mx-auto mb-6">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6b7280"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              <circle cx="12" cy="16" r="1.5" fill="#6b7280" stroke="none" />
            </svg>
          </div>

          <p className="text-xs font-medium tracking-widest uppercase text-gray-400 mb-2">
            Restricted Access
          </p>
          <h2 className="text-xl font-medium text-gray-900 mb-3">
            CRM User Guide
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-8">
            You must be signed in to your Millwoods Travels CRM account to
            access this resource.
          </p>

          <div className="border-t border-gray-100 pt-6 flex flex-col gap-2">
            <Button
              type="submit"
              variant="primary"
              rounded
              onClick={ () => navigate("/login") }
              className="bg-gradient-to-r from-primary/70 to-secondary/70 w-full py-3.5 xl:mr-3"
            >
              {"Sign In"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 🟡 Token ready but URL not yet
  if (!userGuideUrl) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <Spinner label="Loading user guide..." />
      </div>
    );
  }

  // ✅ Authenticated
  const iframeUrl = `${userGuideUrl}${
    userGuideUrl.includes("?") ? "&" : "?"
  }token=${encodeURIComponent(token)}`;

  return (
    <div className="w-full h-screen flex justify-center items-center bg-gray-100 p-4">
      <Helmet>
        <meta charSet="utf-8" />
        <title>CRM User Guide | Millwoods Travels</title>
      </Helmet>

      <div className="w-full h-full p-4 bg-white shadow-lg rounded-lg">
        <iframe
          src={iframeUrl}
          className="w-full h-full"
          title="CRM User Guide"
        />
      </div>
    </div>
  );
}

// Reusable spinner
function Spinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <svg
        className="animate-spin"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#9ca3af"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      </svg>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

export default MillwoodsDocs;
